import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye, X, Loader, BarChart3, ChevronDown, Building2, LayoutList } from "lucide-react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { cn } from "@/lib/utils";
import {
  getContestSalesTotalizer,
  getContestProductSalesDetail,
  getContestProductNotSoldDetail,
  getSalesTotalizerUnities,
  SalesTotalizerResponse,
  ProductSalesDetailResponse,
} from "@/Api/contests";
import { getSaleStatusBadge } from "@/constants/saleStatus";

interface SalesTotalizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  contestId: number;
  contestName: string;
  lotteryName: string;
  lotteryColor: string;
}

interface Unity {
  id: number;
  name: string;
}

// Modo do modal de detalhes: cotas vendidas ou cotas não vendidas
type DetailMode = "sold" | "not_sold";

export const SalesTotalizerModal = ({
  isOpen,
  onClose,
  contestId,
  contestName,
  lotteryName,
  lotteryColor,
}: SalesTotalizerModalProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SalesTotalizerResponse | null>(null);
  const [unities, setUnities] = useState<Unity[]>([]);
  const [unityIdFilter, setUnityIdFilter] = useState<number | null>(null);
  const [selectedUnityName, setSelectedUnityName] = useState<string>("Todas as Vendas");

  // Estado para modal de detalhes das vendas
  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    mode: DetailMode;
    cpClassificationId: number | null;
    productName: string;
    productClassificationName: string;
    data: ProductSalesDetailResponse | null;
  }>({
    isOpen: false,
    loading: false,
    mode: "sold",
    cpClassificationId: null,
    productName: "",
    productClassificationName: "",
    data: null,
  });

  const fetchUnities = async () => {
    try {
      const response = await getSalesTotalizerUnities();
      if (response.ok && response.data?.response) {
        setUnities(response.data.response);
      }
    } catch (error) {
      console.error("Error fetching unities:", error);
    }
  };

  const fetchData = async (unityId: number | null) => {
    setLoading(true);
    try {
      const response = await getContestSalesTotalizer(contestId, undefined, unityId ?? undefined);
      if (response.ok && response.data?.response) {
        setData(response.data.response);
      } else {
        toast.error("Erro ao carregar dados do totalizador");
      }
    } catch (error) {
      console.error("Error fetching sales totalizer:", error);
      toast.error("Erro ao carregar dados do totalizador");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUnities();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && contestId) {
      fetchData(unityIdFilter);
    }
  }, [isOpen, contestId, unityIdFilter]);

  const handleSelectUnity = (unityId: number | null, unityName: string) => {
    setUnityIdFilter(unityId);
    setSelectedUnityName(unityName);
  };

  const handleViewSalesDetail = async (
    cpClassificationId: number,
    productName: string,
    productClassificationName: string,
    mode: DetailMode = "sold"
  ) => {
    setDetailModal({
      isOpen: true,
      loading: true,
      mode,
      cpClassificationId,
      productName,
      productClassificationName,
      data: null,
    });

    try {
      const fetchDetail =
        mode === "not_sold"
          ? getContestProductNotSoldDetail
          : getContestProductSalesDetail;

      const response = await fetchDetail(
        contestId,
        cpClassificationId,
        undefined,
        unityIdFilter ?? undefined
      );
      if (response.ok && response.data?.response) {
        setDetailModal((prev) => ({
          ...prev,
          loading: false,
          data: response.data.response,
        }));
      } else {
        toast.error("Erro ao carregar detalhes das vendas");
        setDetailModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Error fetching sales detail:", error);
      toast.error("Erro ao carregar detalhes das vendas");
      setDetailModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeDetailModal = () => {
    setDetailModal({
      isOpen: false,
      loading: false,
      mode: "sold",
      cpClassificationId: null,
      productName: "",
      productClassificationName: "",
      data: null,
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-amber-600" />
              <div>
                <div className="text-xl font-bold">Totalizador de Vendas</div>
                <div className="text-sm font-normal text-gray-500 flex items-center gap-2">
                  <span
                    className="px-2 py-0.5 rounded text-white text-xs"
                    style={{ backgroundColor: lotteryColor }}
                  >
                    {lotteryName}
                  </span>
                  Concurso {contestName}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-4 mb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <LayoutList className="h-4 w-4 text-amber-600" />
                  {selectedUnityName}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem
                  onClick={() => handleSelectUnity(null, "Todas as Vendas")}
                  className="gap-2"
                >
                  <LayoutList className="h-4 w-4" />
                  Todas as Vendas
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-gray-500">
                  Vendas por Unidade
                </DropdownMenuLabel>
                {unities.map((unity) => (
                  <DropdownMenuItem
                    key={unity.id}
                    onClick={() => handleSelectUnity(unity.id, unity.name)}
                    className="gap-2"
                  >
                    <Building2 className="h-4 w-4" />
                    {unity.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : data ? (
            <ScrollArea className="h-[55vh]">
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <span className="text-2xl font-bold text-blue-600">
                      {data.summary.total_products}
                    </span>
                    <p className="text-xs text-gray-500">Total de Produtos</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <span className="text-2xl font-bold text-green-600">
                      {data.summary.total_quotas_sold}
                    </span>
                    <p className="text-xs text-gray-500">Cotas Vendidas</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <span className="text-2xl font-bold text-amber-600">
                      {data.summary.total_quotas_not_sold}
                    </span>
                    <p className="text-xs text-gray-500">Cotas Não Vendidas</p>
                  </div>
                </div>

                {/* Products Table */}
                <div className="bg-white rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-center">Cotas Vendidas</TableHead>
                        <TableHead className="text-center">Cotas Não Vendidas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.products.map((product) => (
                        <TableRow key={product.cp_classification_id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {product.product_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {product.product_classification.name} -{" "}
                                {Mask.formatCurrency(
                                  product.product_classification.sale_price
                                )}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Badge
                                className={cn(
                                  "text-lg font-bold min-w-[45px] px-3 py-1",
                                  product.quotas_sold > 0
                                    ? "bg-green-500 hover:bg-green-600 text-white"
                                    : "bg-gray-300 text-gray-600"
                                )}
                              >
                                {product.quotas_sold}
                              </Badge>
                              {product.quotas_sold > 0 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 rounded-full hover:bg-green-100"
                                  onClick={() =>
                                    handleViewSalesDetail(
                                      product.cp_classification_id,
                                      product.product_name,
                                      product.product_classification.name
                                    )
                                  }
                                >
                                  <Eye className="h-3.5 w-3.5 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Badge
                                className={cn(
                                  "text-lg font-bold min-w-[45px] px-3 py-1",
                                  product.quotas_not_sold > 0
                                    ? "bg-amber-500 hover:bg-amber-600 text-white"
                                    : "bg-gray-300 text-gray-600"
                                )}
                              >
                                {product.quotas_not_sold}
                              </Badge>
                              {product.quotas_not_sold > 0 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 rounded-full hover:bg-amber-100"
                                  onClick={() =>
                                    handleViewSalesDetail(
                                      product.cp_classification_id,
                                      product.product_name,
                                      product.product_classification.name,
                                      "not_sold"
                                    )
                                  }
                                >
                                  <Eye className="h-3.5 w-3.5 text-amber-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Não foi possível carregar os dados
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes das Vendas */}
      <Dialog open={detailModal.isOpen} onOpenChange={(open) => !open && closeDetailModal()}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold">{detailModal.productName}</div>
                <div className="text-sm text-gray-500 font-normal">
                  {detailModal.productClassificationName}
                  {detailModal.data?.product &&
                    ` - ${Mask.formatCurrency(detailModal.data.product.sale_price)}`}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={closeDetailModal}>
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>

          {detailModal.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : detailModal.data ? (
            <div className="space-y-4">
              {detailModal.mode === "not_sold" ? (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="text-sm text-amber-600">Cotas não vendidas</div>
                  <div className="text-2xl font-bold text-amber-700">
                    {detailModal.data.quotas_not_sold ?? 0}{" "}
                    {(detailModal.data.quotas_not_sold ?? 0) === 1 ? "cota" : "cotas"}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="bg-white/70 rounded p-2">
                      <div className="text-xs text-gray-500">
                        Em vendas em andamento
                      </div>
                      <div className="text-lg font-semibold text-amber-700">
                        {detailModal.data.quotas_in_progress ?? 0}
                      </div>
                    </div>
                    <div className="bg-white/70 rounded p-2">
                      <div className="text-xs text-gray-500">
                        Sem venda em andamento
                      </div>
                      <div className="text-lg font-semibold text-amber-700">
                        {detailModal.data.quotas_without_sale ?? 0}
                      </div>
                    </div>
                  </div>
                  {(detailModal.data.quotas_without_sale ?? 0) > 0 && (
                    <div className="mt-2 text-xs text-amber-700">
                      {detailModal.data.quotas_without_sale} cota
                      {(detailModal.data.quotas_without_sale ?? 0) === 1 ? "" : "s"} ainda
                      não possuem nenhuma venda em andamento registrada.
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-green-600">Cotas vendidas</div>
                  <div className="text-2xl font-bold text-green-700">
                    {detailModal.data.total_quantity}{" "}
                    {detailModal.data.total_quantity === 1 ? "cota" : "cotas"}
                  </div>
                </div>
              )}

              {detailModal.data.sales && detailModal.data.sales.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    {detailModal.mode === "not_sold"
                      ? "Vendas em Andamento"
                      : "Vendas Concluídas"}
                  </h3>
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">ID</TableHead>
                            <TableHead className="text-xs">Cliente</TableHead>
                            <TableHead className="text-xs">Vendedor</TableHead>
                            <TableHead className="text-xs">Unidade</TableHead>
                            <TableHead className="text-xs">Loteria</TableHead>
                            <TableHead className="text-xs">Tipo de Venda</TableHead>
                            <TableHead className="text-xs text-center">Qtd</TableHead>
                            <TableHead className="text-xs">Valor Total</TableHead>
                            <TableHead className="text-xs text-center">Status</TableHead>
                            <TableHead className="text-xs text-center">Data/Hora</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detailModal.data.sales.map((sale) => (
                            <TableRow key={sale.sale_id}>
                              <TableCell className="text-sm font-medium">
                                #{sale.sale_id}
                              </TableCell>
                              <TableCell className="text-sm">
                                {sale.client?.name || "-"}
                              </TableCell>
                              <TableCell className="text-sm">
                                {sale.seller?.name || "-"}
                              </TableCell>
                              <TableCell className="text-sm">
                                {sale.unity?.name || "-"}
                              </TableCell>
                              <TableCell className="text-sm">
                                {sale.lottery?.name || "-"}
                              </TableCell>
                              <TableCell className="text-sm">
                                {sale.sale_type?.name || "-"}
                              </TableCell>
                              <TableCell className="text-sm text-center">
                                {sale.quantity}
                              </TableCell>
                              <TableCell className="text-sm">
                                {Mask.formatCurrency(sale.product_price_total)}
                              </TableCell>
                              <TableCell className="text-center">
                                <span
                                  className={cn(
                                    "px-2.5 py-0.5 rounded text-xs font-medium",
                                    getSaleStatusBadge(sale.sale_status?.id || 1)
                                  )}
                                >
                                  {sale.sale_status?.name || "Concluída"}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="text-sm">
                                  <div>
                                    {sale.sale_created_at
                                      ? new Date(
                                          new Date(sale.sale_created_at).getTime() -
                                            3 * 60 * 60 * 1000
                                        ).toLocaleDateString("pt-BR")
                                      : "-"}
                                  </div>
                                  <div className="text-gray-500 text-xs">
                                    {sale.sale_created_at
                                      ? new Date(
                                          new Date(sale.sale_created_at).getTime() -
                                            3 * 60 * 60 * 1000
                                        ).toLocaleTimeString("pt-BR", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          second: "2-digit",
                                        })
                                      : "-"}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {detailModal.mode === "not_sold"
                    ? "Nenhuma venda em andamento para este produto"
                    : "Nenhuma venda encontrada para este produto"}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Não foi possível carregar os detalhes
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
